import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMins, recurrenceSummary, todayNZ } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  CheckCircle2,
  Loader2,
  ClipboardList,
  Repeat,
  Clock,
  AlertTriangle,
  Send,
  CalendarClock,
  Pencil,
  ListChecks,
  CheckSquare,
  Square,
  Trash2,
} from "lucide-react";
import { TaskEditor } from "@/pages/manager/Tasks";

export default function ManagerTodo() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [date, setDate] = useState(todayNZ());
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState("mine"); // all | mine

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["manager-tasks", date],
    queryFn: async () => (await api.get(`/manager-tasks?date=${date}`)).data,
    refetchInterval: 30000,
  });

  const { data: managers = [] } = useQuery({
    queryKey: ["managers"],
    queryFn: async () => (await api.get("/managers")).data,
  });

  const complete = useMutation({
    mutationFn: (id) => api.post(`/manager-tasks/${id}/complete?date=${date}`),
    onSuccess: (res) => {
      toast.success(`Done — next due ${res.data.next_due_date || "n/a"}`);
      qc.invalidateQueries({ queryKey: ["manager-tasks", date] });
      qc.invalidateQueries({ queryKey: ["task-defs"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const handoff = useMutation({
    mutationFn: (id) => api.post(`/manager-tasks/${id}/handoff?date=${date}`),
    onSuccess: () => {
      toast.success("Sent to cleaners' daily list");
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [deferTask, setDeferTask] = useState(null);
  const defer = useMutation({
    mutationFn: ({ id, occurrence, to_date }) =>
      api.post(`/task-defs/${id}/defer`, { occurrence, to_date }),
    onSuccess: (res) => {
      toast.success(`Deferred to ${res.data.to_date}`);
      setDeferTask(null);
      qc.invalidateQueries({ queryKey: ["manager-tasks", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const skip = useMutation({
    mutationFn: (id) => api.post(`/task-defs/${id}/skip`, { occurrence: date }),
    onSuccess: (res) => {
      toast.success(res.data.next_due_date ? `Removed for today — back on ${res.data.next_due_date}` : "Removed for today");
      qc.invalidateQueries({ queryKey: ["manager-tasks", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const toggleRoom = useMutation({
    mutationFn: ({ taskId, room }) => api.post(`/manager-tasks/${taskId}/room-toggle`, { room_number: room }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["manager-tasks", date] }),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const markAllRooms = useMutation({
    mutationFn: ({ taskId, done }) => api.post(`/manager-tasks/${taskId}/rooms-all`, { done }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["manager-tasks", date] }),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const due = tasks.filter((t) => t.due);

  // Group tasks under each assigned manager. Empty manager_ids = all managers.
  const sortTasks = (list) =>
    [...list].sort((a, b) => (a.due === b.due ? 0 : a.due ? -1 : 1) || (a.next_due_date || "").localeCompare(b.next_due_date || ""));
  const groups = managers.map((m) => ({
    manager: m,
    tasks: sortTasks(tasks.filter((t) => (t.manager_ids || []).length === 0 || t.manager_ids.includes(m.id))),
  }));
  const visibleGroups = filter === "mine" ? groups.filter((g) => g.manager.id === user?.id) : groups;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Manager To-Do</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Scheduled tasks for the team, grouped by manager. Mark done and KlenaPro schedules the next one automatically.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid="todo-filter">
            <button
              type="button"
              onClick={() => setFilter("all")}
              data-testid="todo-filter-all"
              className={`k-press px-3 py-1.5 text-sm font-semibold ${
                filter === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setFilter("mine")}
              data-testid="todo-filter-mine"
              className={`k-press border-l border-border px-3 py-1.5 text-sm font-semibold ${
                filter === "mine" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              Yours
            </button>
          </div>
          <DatePicker value={date} onChange={setDate} testid="todo-date-picker" />
        </div>
      </div>

      {due.length > 0 && (
        <div
          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800"
          data-testid="todo-due-banner"
        >
          <AlertTriangle className="h-4 w-4" />
          {due.length} task{due.length > 1 ? "s are" : " is"} due.
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : tasks.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 rounded-md border border-dashed py-20 text-center">
          <ClipboardList className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No manager tasks yet. Create one in Scheduled Tasks (e.g. Evacuation trial every 3 months).
          </p>
        </Card>
      ) : (
        <div className="space-y-7">
          {visibleGroups.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 rounded-md border border-dashed py-16 text-center">
              <ClipboardList className="h-7 w-7 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {filter === "mine" ? "No tasks assigned to you." : "No managers to show."}
              </p>
            </Card>
          ) : (
            visibleGroups.map(({ manager, tasks: mtasks }) => (
              <ManagerGroup
                key={manager.id}
                manager={manager}
                tasks={mtasks}
                onComplete={complete.mutate}
                onHandoff={handoff.mutate}
                onDefer={setDeferTask}
                onSkip={skip.mutate}
                onEdit={setEditingTask}
                onToggleRoom={(taskId, room) => toggleRoom.mutate({ taskId, room })}
                onMarkAllRooms={(taskId, done) => markAllRooms.mutate({ taskId, done })}
                pending={complete.isPending || handoff.isPending}
              />
            ))
          )}
        </div>
      )}

      {deferTask && (
        <DeferDialog task={deferTask} onClose={() => setDeferTask(null)} onDefer={defer.mutate} pending={defer.isPending} />
      )}

      {editingTask && (
        <TaskEditor
          task={{ ...editingTask, assignee_type: "manager", new: false }}
          onClose={() => setEditingTask(null)}
          onSaved={() => {
            setEditingTask(null);
            qc.invalidateQueries({ queryKey: ["manager-tasks"] });
          }}
        />
      )}
    </div>
  );
}

function ManagerGroup({ manager, tasks, onComplete, onHandoff, onDefer, onSkip, onEdit, onToggleRoom, onMarkAllRooms, pending }) {
  const dueCount = tasks.filter((t) => t.due).length;
  return (
    <div className="space-y-2.5" data-testid={`todo-group-${manager.id}`}>
      <div className="flex items-center gap-2 border-b border-border pb-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary font-display text-sm font-bold">
          {manager.name.charAt(0).toUpperCase()}
        </div>
        <span className="font-display text-lg font-extrabold">{manager.name}</span>
        <span className="text-sm text-muted-foreground">
          · {tasks.length} task{tasks.length === 1 ? "" : "s"}{dueCount > 0 ? ` · ${dueCount} due` : ""}
        </span>
      </div>
      {tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing assigned — all caught up.</p>
      ) : (
        <div className="space-y-2.5">
          {tasks.map((t) => (
            <TaskRow
              key={t.id}
              t={t}
              onComplete={onComplete}
              onHandoff={onHandoff}
              onDefer={onDefer}
              onSkip={onSkip}
              onEdit={onEdit}
              onToggleRoom={onToggleRoom}
              onMarkAllRooms={onMarkAllRooms}
              pending={pending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskRow({ t, onComplete, onHandoff, onDefer, onSkip, onEdit, onToggleRoom, onMarkAllRooms, pending }) {
  const [showRooms, setShowRooms] = useState((t.rooms_total || 0) > 0 && t.due);
  const hasRooms = (t.rooms_total || 0) > 0;
  const allDone = hasRooms && t.rooms_done >= t.rooms_total;
  return (
    <Card
      data-testid={`todo-row-${t.name}`}
      className={`flex flex-col gap-3 rounded-md border p-4 ${
        t.overdue ? "border-amber-300 bg-amber-50/40" : ""
      } ${t.due ? "" : "opacity-80"}`}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <div className="flex-1">
          <div className="font-display text-lg font-extrabold">{t.name}</div>
          {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/50 px-2 py-1 font-semibold">
              <Repeat className="h-3 w-3" /> {recurrenceSummary(t.recurrence)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/50 px-2 py-1 font-semibold">
              <Clock className="h-3 w-3" /> {fmtMins(t.est_minutes)}
            </span>
            {hasRooms && (
              <button
                type="button"
                onClick={() => setShowRooms((s) => !s)}
                data-testid={`room-progress-${t.name}`}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 font-semibold ${
                  allDone ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-sky-300 bg-sky-50 text-sky-700"
                }`}
              >
                <ListChecks className="h-3 w-3" /> {t.rooms_done}/{t.rooms_total} rooms
              </button>
            )}
            {(t.manager_ids || []).length === 0 && (
              <span className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 font-semibold text-indigo-700">
                All managers
              </span>
            )}
            <span className={`font-medium ${t.overdue ? "text-amber-700" : "text-muted-foreground"}`}>
              {t.overdue ? "Overdue · " : "Due "}
              {t.next_due_date}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() => onEdit(t)}
            data-testid={`edit-task-${t.name}`}
            className="k-press gap-1.5 text-muted-foreground"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() => onDefer(t)}
            data-testid={`defer-task-${t.name}`}
            className="k-press gap-1.5 text-muted-foreground"
          >
            <CalendarClock className="h-3.5 w-3.5" /> Defer
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() => { if (window.confirm(`Remove "${t.name}" from today's list? It will come back on its next scheduled day.`)) onSkip(t.id); }}
            data-testid={`skip-task-${t.name}`}
            className="k-press gap-1.5 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove today
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pending}
            onClick={() => onHandoff(t.id)}
            data-testid={`handoff-task-${t.name}`}
            className="k-press gap-1.5"
          >
            <Send className="h-3.5 w-3.5" /> To cleaners
          </Button>
          <Button
            size="sm"
            disabled={pending || (hasRooms && !allDone)}
            onClick={() => onComplete(t.id)}
            data-testid={`complete-task-${t.name}`}
            title={hasRooms && !allDone ? "Tick all rooms first" : undefined}
            className="k-press gap-1.5 bg-emerald-600 hover:bg-emerald-700"
          >
            <CheckCircle2 className="h-4 w-4" /> Mark done
          </Button>
        </div>
      </div>

      {hasRooms && showRooms && (
        <div className="rounded-md border border-border bg-secondary/20 p-3" data-testid={`room-checklist-${t.name}`}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-muted-foreground">
              Tick each room as you complete it — progress is saved until all rooms are done.
            </span>
            <button
              type="button"
              disabled={pending}
              onClick={() => onMarkAllRooms(t.id, !allDone)}
              data-testid={`room-tick-all-${t.name}`}
              className="k-press shrink-0 rounded-md border border-primary px-2.5 py-1 text-xs font-bold text-primary hover:bg-primary/10"
            >
              {allDone ? "Untick all" : "Tick all"}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {t.room_checklist.map((rn) => {
              const done = !!(t.room_progress || {})[rn];
              return (
                <button
                  key={rn}
                  type="button"
                  disabled={pending}
                  onClick={() => onToggleRoom(t.id, rn)}
                  data-testid={`room-check-${t.name}-${rn}`}
                  className={`k-press inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-semibold ${
                    done
                      ? "border-emerald-400 bg-emerald-100 text-emerald-800"
                      : "border-border bg-background text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {done ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                  {rn}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function DeferDialog({ task, onClose, onDefer, pending }) {
  const occurrence = task.next_due_date || todayNZ();
  const nextDay = new Date(occurrence + "T00:00:00");
  nextDay.setDate(nextDay.getDate() + 1);
  const [toDate, setToDate] = useState(nextDay.toISOString().slice(0, 10));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="defer-dialog">
        <DialogHeader>
          <DialogTitle>Defer "{task.name}"</DialogTitle>
          <DialogDescription>
            Move just this occurrence to a later date. The task's normal recurrence is not changed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>New date (currently due {occurrence})</Label>
          <Input
            type="date"
            min={nextDay.toISOString().slice(0, 10)}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="defer-date-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={pending || !toDate}
            onClick={() => onDefer({ id: task.id, occurrence, to_date: toDate })}
            data-testid="defer-confirm-btn"
            className="gap-2"
          >
            <CalendarClock className="h-4 w-4" /> Defer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



